/**
 * MonthlyVerification — Phase 24
 * Printable per-class per-month verification sheet. PDF-first; thin UI.
 *
 * Operational use case: principal/office staff print the sheet, physically
 * tick each student against the receipt book, signatures at bottom, file.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ClipboardText,
  DownloadSimple,
  FilePdf,
  Eye,
  CheckCircle,
  WarningCircle,
  Circle,
  XCircle,
  Minus,
} from "@phosphor-icons/react";
import api, { API_BASE, tokenStore, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const STATUS_TONE = {
  PAID: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PARTIAL: "bg-amber-50 text-amber-800 border-amber-200",
  UNPAID: "bg-rose-50 text-rose-700 border-rose-200",
  WAIVED: "bg-indigo-50 text-indigo-700 border-indigo-200",
  "N/A": "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_ICON = {
  PAID: CheckCircle,
  PARTIAL: WarningCircle,
  UNPAID: Circle,
  WAIVED: XCircle,
  "N/A": Minus,
};

// Map backend's short concession_tag (CONCESSION_TAG_MAP in report_service.py)
// to a parent-friendly label. Keeps the WAIVED row in sync with the actual
// concession reason — previously this column hardcoded "RTE — full waiver".
const WAIVER_LABEL = {
  RTE: "RTE",
  SIB: "Sibling discount",
  STAFF: "Staff child",
  SCH: "Scholarship",
  MERIT: "Merit",
  NEG: "Negotiated",
  OTH: "Other",
};

async function streamFile(url, filename) {
  try {
    const token = tokenStore.get();
    const res = await fetch(`${API_BASE}${url}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(`HTTP ${res.status}: ${t.slice(0, 200)}`);
    }
    const blob = await res.blob();
    const obj = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = obj;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(obj);
  } catch (e) {
    toast.error(`Download failed: ${e.message}`);
  }
}

// Generate YYYY-MM list spanning session start..end.
function buildMonthOptions(session) {
  if (!session) return [];
  const start = session.start_date ? new Date(session.start_date) : null;
  const end = session.end_date ? new Date(session.end_date) : null;
  if (!start || !end) return [];
  const out = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cur <= last && out.length < 24) {
    const ym = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}`;
    const label = cur.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
    out.push({ ym, label });
    cur.setMonth(cur.getMonth() + 1);
  }
  return out;
}

export default function MonthlyVerification() {
  const { user } = useAuth();
  const isTeacher = user?.role === "teacher";
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [periodMonth, setPeriodMonth] = useState("");
  const [sort, setSort] = useState("name");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Initial load: sessions + classes
  // Phase 28 — teachers fetch only their assigned classes; admin/cashier
  // continue to receive the full class list.
  useEffect(() => {
    (async () => {
      try {
        const [{ data: ss }, { data: cs }] = await Promise.all([
          api.get("/school/sessions"),
          api.get(isTeacher ? "/school/teacher/me/classes" : "/school/classes"),
        ]);
        setSessions(ss);
        setClasses(cs);
        const active = ss.find((x) => x.is_active);
        if (active) setSessionId(active.id);
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, [isTeacher]);

  const activeSession = useMemo(
    () => sessions.find((s) => s.id === sessionId),
    [sessions, sessionId]
  );
  const monthOptions = useMemo(() => buildMonthOptions(activeSession),
    [activeSession]);
  const filteredClasses = useMemo(
    () => classes.filter((c) => !sessionId || c.session_id === sessionId),
    [classes, sessionId]
  );

  // Set default period_month = current month (clamped to session window)
  useEffect(() => {
    if (!periodMonth && monthOptions.length > 0) {
      const now = new Date();
      const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const hit = monthOptions.find((o) => o.ym === cur);
      setPeriodMonth(hit ? cur : monthOptions[0].ym);
    }
  }, [monthOptions, periodMonth]);

  const canLoad = sessionId && classId && periodMonth;

  const load = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true);
    setError("");
    try {
      const { data: d } = await api.get(
        `/school/reports/monthly-verification`,
        { params: { session_id: sessionId, class_id: classId, period_month: periodMonth, sort } }
      );
      setData(d);
    } catch (e) {
      setData(null);
      setError(formatApiError(e));
    }
    setLoading(false);
  }, [sessionId, classId, periodMonth, sort, canLoad]);

  // Auto-load when filters complete
  useEffect(() => {
    if (canLoad) {
      const t = setTimeout(load, 200);
      return () => clearTimeout(t);
    }
  }, [load, canLoad]);

  const baseQuery = `session_id=${sessionId}&class_id=${classId}&period_month=${periodMonth}&sort=${sort}`;
  const pdfUrl = `${API_BASE}/school/reports/exports/monthly-verification.pdf?${baseQuery}`;
  const csvUrl = `/school/reports/exports/monthly-verification.csv?${baseQuery}`;

  const downloadPdf = () => {
    const cls = data?.class?.label || "class";
    const safe = cls.replace(/[^A-Za-z0-9]/g, "_");
    streamFile(
      `/school/reports/exports/monthly-verification.pdf?${baseQuery}`,
      `monthly-verification-${safe}-${periodMonth}.pdf`,
    );
  };

  const downloadCsv = () => {
    const cls = data?.class?.label || "class";
    const safe = cls.replace(/[^A-Za-z0-9]/g, "_");
    streamFile(csvUrl, `monthly-verification-${safe}-${periodMonth}.csv`);
  };

  const previewPdf = async () => {
    if (!canLoad) return;
    try {
      const token = tokenStore.get();
      const res = await fetch(`${API_BASE}${`/school/reports/exports/monthly-verification.pdf?${baseQuery}`}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const obj = URL.createObjectURL(blob);
      window.open(obj, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(`Preview failed: ${e.message}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="monthly-verification-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center gap-1.5">
            <ClipboardText size={12} weight="duotone" /> Reports · Monthly Verification
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">
            Monthly verification sheet
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Printable single-sheet roll for one class, one month. Office staff
            tick verified rows against the receipt book, signatures at the bottom,
            file for audit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm" className="gap-1.5">
            <a href="/reports" data-testid="back-to-reports">← Back to reports</a>
          </Button>
        </div>
      </div>

      {/* Phase 28 — Teacher with no class assignments: friendly empty state.
          Backend will 403 anyway; this is the UX-first fence. */}
      {isTeacher && classes.length === 0 && (
        <div
          className="p-4 border border-amber-200 bg-amber-50 rounded-lg text-sm text-amber-900"
          data-testid="teacher-empty-state"
        >
          You don't have any classes assigned yet. Ask your school admin to
          assign a class to you to use Monthly Verification.
        </div>
      )}

      {/* Filter bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4 border border-slate-200 bg-white rounded-lg" data-testid="mv-filters">
        <div>
          <Label className="text-xs">Session</Label>
          <Select value={sessionId} onValueChange={(v) => { setSessionId(v); setClassId(""); setPeriodMonth(""); }}>
            <SelectTrigger className="mt-1" data-testid="mv-session"><SelectValue placeholder="Session" /></SelectTrigger>
            <SelectContent>
              {sessions.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}{s.is_active ? " · Active" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Class</Label>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger className="mt-1" data-testid="mv-class"><SelectValue placeholder="Pick a class" /></SelectTrigger>
            <SelectContent>
              {filteredClasses.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}{c.section ? ` - ${c.section}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Month</Label>
          <Select value={periodMonth} onValueChange={setPeriodMonth} disabled={monthOptions.length === 0}>
            <SelectTrigger className="mt-1" data-testid="mv-month"><SelectValue placeholder="Pick a month" /></SelectTrigger>
            <SelectContent>
              {monthOptions.map((o) => (
                <SelectItem key={o.ym} value={o.ym}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Sort by</Label>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="mt-1" data-testid="mv-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name (A–Z)</SelectItem>
              <SelectItem value="admission_no">Admission No</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Action row */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          onClick={downloadPdf} disabled={!canLoad || loading}
          className="gap-1.5"
          data-testid="mv-download-pdf"
        >
          <FilePdf size={14} weight="bold" /> Download PDF
        </Button>
        <Button
          variant="outline" onClick={previewPdf} disabled={!canLoad || loading}
          className="gap-1.5"
          data-testid="mv-preview-pdf"
        >
          <Eye size={14} /> Preview
        </Button>
        <Button
          variant="outline" onClick={downloadCsv} disabled={!canLoad || loading}
          className="gap-1.5"
          data-testid="mv-download-csv"
        >
          <DownloadSimple size={14} /> CSV
        </Button>
        {!canLoad && (
          <span className="text-xs text-slate-500 ml-2">
            Pick session, class and month to enable downloads.
          </span>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md" data-testid="mv-error">
          {error}
        </div>
      )}

      {/* Summary preview (NOT the PDF — a screen affordance only) */}
      {data && data.summary && (
        <div className="border border-slate-200 bg-white rounded-lg p-4 space-y-3" data-testid="mv-summary">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
                Preview · printable PDF on download
              </div>
              <div className="font-semibold text-slate-900 font-heading text-lg mt-0.5">
                Class {data.class?.label}  ·  {data.period_label}
              </div>
            </div>
            <div className="text-xs text-slate-500">
              Session {data.session?.name}{data.session?.is_active ? " · Active" : ""}
            </div>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
            <Stat label="Headcount" value={data.summary.headcount} tone="slate" />
            <Stat label="Paid" value={data.summary.paid} tone="emerald" />
            <Stat label="Partial" value={data.summary.partial} tone="amber" />
            <Stat label="Unpaid" value={data.summary.unpaid} tone="rose" />
            <Stat label="Waived" value={data.summary.waived} tone="indigo" />
          </div>
          {/* Phase 25C — money strip hidden when backend strips totals
              (cashier role). Per-row amounts in the preview table remain. */}
          {(data.summary.expected !== undefined ||
            data.summary.collected !== undefined ||
            data.summary.pending !== undefined) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <Money label="Expected" value={data.summary.expected} />
              <Money label="Collected (capped)" value={data.summary.collected} tone="emerald" />
              <Money label="Pending" value={data.summary.pending} tone="rose" />
            </div>
          )}
          {/* Phase 30K-pre — RAW vs CAPPED clarification for MV summary.
              When per-row paid amounts (RAW) exceed the capped headline,
              we surface the gap so the operator doesn't think money is
              missing. Only renders if there's an actual delta. */}
          {(() => {
            const rowRawSum = (data.students || []).reduce(
              (a, r) => a + (Number(r.paid) || 0), 0,
            );
            const capped = Number(data.summary.collected || 0);
            const delta = Math.round((rowRawSum - capped) * 100) / 100;
            if (delta <= 0.5) return null;
            return (
              <div
                className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                data-testid="mv-overcollection-note"
              >
                <strong>Reconciliation note:</strong> Per-row &ldquo;Paid&rdquo; column shows RAW receipts; the
                &ldquo;Collected (capped)&rdquo; headline above caps each cell at the genuine liability. Δ ={" "}
                {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(delta)}{" "}
                of over-collection in this class+month is visible in the row totals but excluded from the headline.
              </div>
            );
          })()}
          {data.annual_receipts_count > 0 && (
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
              <strong>Note:</strong> Monthly fee heads only.{" "}
              {data.annual_receipts_count} annual receipt(s) collected this month —
              see Payments → {data.period_label} for details.
            </div>
          )}

          {/* Row preview table (compact, screen-only — PDF is the canonical output) */}
          <div className="overflow-x-auto -mx-4 sm:mx-0 mt-2">
            <table className="min-w-full text-sm border-t border-slate-200" data-testid="mv-rows-table">
              <thead className="bg-slate-50">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="text-left font-semibold px-3 py-2">#</th>
                  <th className="text-left font-semibold px-3 py-2">Adm. No</th>
                  <th className="text-left font-semibold px-3 py-2">Student</th>
                  <th className="text-left font-semibold px-3 py-2">Status</th>
                  <th className="text-right font-semibold px-3 py-2">Expected</th>
                  <th className="text-right font-semibold px-3 py-2">Paid</th>
                  <th className="text-right font-semibold px-3 py-2">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.students.length === 0 ? (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">No students in this class.</td></tr>
                ) : data.students.map((r) => {
                  const Icon = STATUS_ICON[r.status] || Circle;
                  return (
                    <tr key={r.student_id} data-testid={`mv-row-${r.student_id}`}>
                      <td className="px-3 py-2 text-slate-700 font-mono text-xs">{r.seq}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600">{r.admission_no}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">{r.name}</div>
                        {r.receipts && r.receipts.length > 0 ? (
                          <div className="text-[11px] text-slate-500 font-mono">
                            {r.receipts[0].receipt_no} · {r.receipts[0].mode} · {r.receipts[0].date_label}
                            {r.receipts[0].collected_by_name ? (
                              <span className="ml-1 text-slate-600">
                                · by {r.receipts[0].collected_by_name.split(" ")[0]}
                                {" "}
                                ({r.receipts[0].collected_by_role === "teacher" ? "T" : "A"})
                              </span>
                            ) : null}
                            {r.receipts.length > 1 ? ` (+${r.receipts.length - 1})` : ""}
                          </div>
                        ) : r.status === "WAIVED" ? (
                          <div className="text-[11px] text-indigo-700">
                            {WAIVER_LABEL[r.concession_tag] || r.concession_tag || "RTE"} — full waiver
                          </div>
                        ) : r.status === "N/A" ? (
                          <div className="text-[11px] text-slate-500">Joined {r.joined_at}</div>
                        ) : (
                          <div className="text-[11px] text-slate-400">— no payment recorded —</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded border ${STATUS_TONE[r.status]}`}>
                          <Icon size={12} weight="fill" /> {r.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-800">
                        {r.status === "N/A" ? "—" : formatINR(r.expected)}
                        {r.concession_delta > 0 && r.concession_tag && (
                          <span className="ml-1 text-[10px] font-semibold text-amber-700">
                            {r.status === "WAIVED" ? r.concession_tag : `−${formatINR(r.concession_delta)} ${r.concession_tag}`}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-700">
                        {r.paid > 0 ? formatINR(r.paid) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {r.balance > 0 ? (
                          <span className="text-rose-700 font-semibold">{formatINR(r.balance)}</span>
                        ) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-slate-400">
            ☑ The downloaded PDF is the canonical document. This screen view is a quick reference.
          </div>
        </div>
      )}

      {/* Loading placeholder */}
      {!data && canLoad && loading && (
        <div className="p-6 text-center text-slate-500 border border-slate-200 bg-white rounded-lg">
          Loading verification sheet…
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  }[tone];
  return (
    <div className={`px-3 py-2 rounded-md border ${tones}`} data-testid={`mv-stat-${label.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-lg font-bold font-heading">{value}</div>
    </div>
  );
}

function Money({ label, value, tone = "slate" }) {
  const formatINRfull = (n) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency", currency: "INR", maximumFractionDigits: 0,
    }).format(Number(n) || 0);
  const tones = {
    slate: "bg-slate-50 text-slate-800 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rose: "bg-rose-50 text-rose-800 border-rose-200",
  }[tone];
  return (
    <div className={`px-3 py-2 rounded-md border ${tones}`} data-testid={`mv-money-${label.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-lg font-bold font-mono">{formatINRfull(value)}</div>
    </div>
  );
}
